import React from "react";
import { Formik, FormikProps, Field, FieldProps } from "formik";
import { Grid, MenuItem, Hidden, Tooltip } from "@material-ui/core";
import * as Yup from "yup";
import { Values } from "Tools/Values";
import renderCommonFormField, { STTextField } from "@src/Components/Common/FormFields/renderCommonFormField";
import FormSwitch from "@src/Components/Common/FormFields/FormSwitch/FormSwitch";
import { observer, inject } from "mobx-react";
import { IValuesStore } from "@src/Stores/Values/types";
import { STFormTitleTypography } from "@src/Components/Ui/STFormTitleTypohraphy";
import _ from "lodash";
import { IManagementStore } from "@src/Stores/Management/types";
import { AuthProvider } from "@src/Interfaces/Entity/OAuthProvider";
import { computed } from "mobx";
import { STFormWrapper, STTypeGrid, STInputLabelType, STStateGrid, STSelect, STTypeSelectGrid } from "./styles";
import { ButtonsForm } from "@src/Components/Common/ButtonsForm/ButtonsForm";
import FileField from "./FileField/FileField";
import { STContentWrapper } from "@src/Components/Common/SimpleModal/styles";

function validateIfRequired(parent: any) {
  return function (value: string) {
    if (parent.type || value) {
      return true;
    }
    return false;
  };
}

const FIVE_MB = 5e6;

const oAuthSchema = () =>
  Yup.object().shape({
    name: Yup.string().required(() => {
      return Values.getLocalizationValue("fieldRequired");
    }),
    displayTitle: Yup.string().required(() => {
      return Values.getLocalizationValue("fieldRequired");
    }),
    clientId: Yup.string().required(() => {
      return Values.getLocalizationValue("fieldRequired");
    }),
    clientSecret: Yup.string().required(() => {
      return Values.getLocalizationValue("fieldRequired");
    }),
    fileImage: Yup.mixed().when("type", {
      is: (type: number) => type === 0,
      then: Yup.mixed().test(
        "maxSize",
        () => Values.getLocalizationValue("maxSize"),
        (value) => !value || value.size < FIVE_MB
      ),
    }),
    settings: Yup.object().when("type", {
      is: (type: number) => type === 0,
      then: Yup.object().shape({
        tokenUrl: Yup.string().test(
          "tokenRequired",
          Values.getLocalizationValue("fieldRequired"),
          validateIfRequired("")
        ),
        authorizeUrl: Yup.string().test(
          "authorizeUrlRequired",
          Values.getLocalizationValue("fieldRequired"),
          validateIfRequired("")
          ),
        meUrl: Yup.string().test(
          "meUrlRequired",
           Values.getLocalizationValue("fieldRequired"),
           validateIfRequired("")
        ),
        scope: Yup.string().test("scopeRequired", Values.getLocalizationValue("fieldRequired"), validateIfRequired("")),
      }),
    }),
  });

interface IProps {
  title: string;
  initialValues: AuthProvider;
  onSubmit: (values: AuthProvider) => void;
  onClose: () => void;
  values?: IValuesStore;
  management?: IManagementStore;
}

@inject("values", "management")
@observer
class AuthProviderForm extends React.Component<IProps> {
  @computed get isEditMode(): boolean {
    return this.props.initialValues.name !== "";
  }
  handleProtocolTypeChange = (event: any, formikProps: any) => {
    const newType = parseInt(event.target.value);
    formikProps.setFieldValue("protocolType", newType);
  };
  handleProviderTypeChange = (event: any, formikProps: any) => {
      const newType = parseInt(event.target.value);
      const currentName = formikProps.values.name;
      formikProps.setFieldValue("type", newType);
      const newTypeName = Object.entries(this.props.values.oAuthProviderTypes).filter(
          (entry: [string, number]) => entry[1] === newType
      )[0][0];
      const isDefaultName = Object.entries(this.props.values.oAuthProviderTypes).find(
          (entry: [string, number]) => entry[0] === currentName
      );
      if (newType === this.props.values.oAuthProviderTypes.Custom && currentName && isDefaultName) {
          formikProps.setFieldValue("name", "");
      } else if (!currentName || isDefaultName) {
          formikProps.setFieldValue("name", newTypeName);
      }
      if (newType != this.props.values.oAuthProviderTypes.Custom) {
          formikProps.setFieldValue("protocolType", this.props.values.authProtocolTypes.OpenId);
      }
  };
  render() {
    return (
      <Formik
        enableReinitialize
        initialValues={this.props.initialValues}
        validationSchema={() => oAuthSchema()}
        onSubmit={this.props.onSubmit}
        render={(formikProps: FormikProps<AuthProvider>) => (
          <STFormWrapper onSubmit={formikProps.handleSubmit}>
            <STFormTitleTypography>{this.props.title}</STFormTitleTypography>
            <STContentWrapper>
              <STTypeGrid container alignItems="center">

                <STTypeSelectGrid>
                  <STInputLabelType>{Values.getLocalizationValue("type")}</STInputLabelType>
                  <STSelect
                    value={formikProps.values.type}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.handleProviderTypeChange(event, formikProps)}
                    disabled={this.isEditMode}
                  >
                    {Object.entries(this.props.values.oAuthProviderTypes || {})
                      .sort()
                      .map((key) => (
                        <MenuItem key={key[1]} value={key[1]}>
                          {key[0]}
                        </MenuItem>
                      ))}
                    </STSelect>
                </STTypeSelectGrid>
                <STTypeSelectGrid>
                    <STInputLabelType>{Values.getLocalizationValue("protocolType")}</STInputLabelType>
                    <STSelect
                        value={formikProps.values.protocolType}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.handleProtocolTypeChange(event, formikProps)}
                        disabled={this.isEditMode || formikProps.values.type != this.props.values.oAuthProviderTypes.Custom}
                    >
                        {Object.entries(this.props.values.authProtocolTypes || {})
                            .sort()
                            .map((key) => (
                                <MenuItem key={key[1]} value={key[1]}>
                                    {key[0]}
                                </MenuItem>
                            ))}
                    </STSelect>
                </STTypeSelectGrid>


              </STTypeGrid>
              {renderCommonFormField<AuthProvider>("name", Values.getLocalizationValue("name"), formikProps)}
              {renderCommonFormField<AuthProvider>("displayTitle", Values.getLocalizationValue("displayTitle"), formikProps)}
              {formikProps.values.type === this.props.values.oAuthProviderTypes.Custom && (
                <FileField<AuthProvider>
                  fieldName="fileImage"
                  label={Values.getLocalizationValue("image")}
                  formikProps={formikProps}
                  imageUrl={this.props.initialValues.iconUrl}
                />
              )}
              {renderCommonFormField<AuthProvider>("clientId", Values.getLocalizationValue("clientId"), formikProps)}
              {renderCommonFormField<AuthProvider>(
                "clientSecret",
                Values.getLocalizationValue("clientSecret"),
                formikProps,
                true,
                null,
                false,
                "password"
              )}
              {formikProps.values.type === this.props.values.oAuthProviderTypes.Custom && (
                <Field
                  name="settings.tokenUrl"
                  render={({ field }: FieldProps) => (
                    <STTextField
                      {...field}
                      fullWidth
                      label={Values.getLocalizationValue("tokenUrl")}
                      error={
                        formikProps.touched.settings &&
                        formikProps.errors.settings &&
                        formikProps.touched.settings["tokenUrl"] &&
                        !!formikProps.errors.settings["tokenUrl"]
                      }
                      helperText={
                        formikProps.touched.settings &&
                        formikProps.errors.settings &&
                        formikProps.touched.settings["tokenUrl"] &&
                        formikProps.errors.settings["tokenUrl"]
                      }
                    />
                  )}
                />
              )}
              {formikProps.values.type === this.props.values.oAuthProviderTypes.Custom && (
                <Field
                  name="settings.authorizeUrl"
                  render={({ field }: FieldProps) => (
                    <STTextField
                      {...field}
                      fullWidth
                      label={Values.getLocalizationValue("authorizeUrl")}
                      error={
                        formikProps.touched.settings &&
                        formikProps.errors.settings &&
                        formikProps.touched.settings["authorizeUrl"] &&
                        !!formikProps.errors.settings["authorizeUrl"]
                      }
                      helperText={
                        formikProps.touched.settings &&
                        formikProps.errors.settings &&
                        formikProps.touched.settings["authorizeUrl"] &&
                        formikProps.errors.settings["authorizeUrl"]
                      }
                    />
                  )}
                />
              )}
            {formikProps.values.type === this.props.values.oAuthProviderTypes.Custom && (
                <Field
                    name="settings.meUrl"
                    render={({ field }: FieldProps) => (
                        <STTextField
                            {...field}
                            fullWidth
                            label={Values.getLocalizationValue("meUrl")}
                            error={
                                formikProps.touched.settings &&
                                formikProps.errors.settings &&
                                formikProps.touched.settings["meUrl"] &&
                                !!formikProps.errors.settings["meUrl"]
                            }
                            helperText={
                                formikProps.touched.settings &&
                                formikProps.errors.settings &&
                                formikProps.touched.settings["meUrl"] &&
                                formikProps.errors.settings["meUrl"]
                            }
                        />
                    )}
                />
            )}
              {formikProps.values.type === this.props.values.oAuthProviderTypes.Custom && (
                <Field
                  name="settings.scope"
                  render={({ field }: FieldProps) => (
                    <STTextField
                      {...field}
                      fullWidth
                      label={Values.getLocalizationValue("scope")}
                      error={
                        formikProps.touched.settings &&
                        formikProps.errors.settings &&
                        formikProps.touched.settings["scope"] &&
                        !!formikProps.errors.settings["scope"]
                      }
                      helperText={
                        formikProps.touched.settings &&
                        formikProps.errors.settings &&
                        formikProps.touched.settings["scope"] &&
                        formikProps.errors.settings["scope"]
                      }
                    />
                  )}
                />
              )}
              <STStateGrid container alignItems="center">
                <Grid item>{Values.getLocalizationValue("active")}</Grid>
                <Grid item>
                  <FormSwitch<AuthProvider>
                    trueAs={this.props.values.states.Active}
                    falseAs={this.props.values.states.Deactivated}
                    fieldName="state"
                    formikProps={formikProps}
                  />
                </Grid>
              </STStateGrid>
                <Tooltip title={!formikProps.values.canBeRequired ? Values.getLocalizationValue("tooltipMustBeUsedAtLeastOnce") : ""} placement={"top"}>
                  <STStateGrid container alignItems="center">
                    <Grid item>{Values.getLocalizationValue("required")}</Grid>
                    <Grid item>
                      <FormSwitch<AuthProvider>
                        trueAs={true}
                        falseAs={false}
                        fieldName="isRequired"
                        formikProps={formikProps}
                        disabled={!formikProps.values.canBeRequired}
                      />
                    </Grid>
                  </STStateGrid>
                </Tooltip>
            </STContentWrapper>
            <ButtonsForm onSubmit={formikProps.handleSubmit} onClose={this.props.onClose} />
          </STFormWrapper>
        )}
      />
    );
  }
}

export default AuthProviderForm;
